import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import getLoansList from '../../api/GetLoansList';
import { setCurrentMember } from '../../store/slices/membersSlice';

const ActivityItem = ({ user, action, book, time, delay, onClick }) => (
    <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay, duration: 0.3 }}
        onClick={onClick}
        className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5 cursor-pointer"
    >
        <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full border border-white/10" />
        <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">
                <span className="font-medium">{user.name}</span>
                <span className="text-slate-400"> {action} </span>
                <span className="font-medium text-primary">{book}</span>
            </p>
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                <Clock size={12} />
                {time}
            </p>
        </div>
    </motion.div>
);

export default function RecentActivity({ onNavigate }) {
    const [activities, setActivities] = useState([]);
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.currentUser);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const { items: books } = useSelector((state) => state.books);
    const { items: members } = useSelector((state) => state.members);

    const handleMemberClick = (memberData) => {
        if (memberData) {
            dispatch(setCurrentMember(memberData));
            if (onNavigate) {
                onNavigate('Members');
            }
        }
    };

    useEffect(() => {
        const fetchLoans = async () => {
            //console.log("RecentActivity: Fetching loans...", { API_BASE, token, code_etablissement });
            try {
                let loans = await getLoansList(API_BASE, token, code_etablissement);

                // If student, filter for personal activity only
                if (currentUser.role === 'Étudiant') {
                    const myId = currentUser.id || currentUser.ID;
                    loans = loans.filter(loan => loan.user_id == myId);
                }

                // Sort by most recent first and take top 5
                const sortedLoans = loans.sort((a, b) => {
                    const dateA = new Date(b.start_date || b.end_date);
                    const dateB = new Date(a.start_date || a.end_date);
                    return dateA - dateB;
                }).slice(0, 8);

                // Map loans to activity format
                const formattedActivities = sortedLoans.map(loan => {
                    const member = members.find(m => (m.ID || m.id) == loan.user_id);

                    // Find book in store by item_id
                    const book = books.find(b => (b.item_id || b.id) == loan.item_id);
                    const bookTitle = book?.title || loan.item?.manifestation?.title || "Livre inconnu";

                    // Determine action based on end_date
                    const isReturned = loan.end_date && !loan.end_date.startsWith("0001");
                    let action = isReturned ? "a rendu" : "a emprunté";

                    if (!isReturned && book?.status === 'Réservé') {
                        action = "a réservé";
                    }

                    // Calculate time ago
                    const date = new Date(isReturned ? loan.end_date : loan.start_date);
                    const now = new Date();
                    const diffMs = now - date;
                    const diffMins = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMins / 60);
                    const diffDays = Math.floor(diffHours / 24);

                    let timeAgo;
                    if (diffMins < 60) {
                        timeAgo = `il y a ${diffMins} min`;
                    } else if (diffHours < 24) {
                        timeAgo = `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
                    } else {
                        timeAgo = `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
                    }

                    return {
                        user: {
                            name: member?.name || "Utilisateur inconnu",
                            avatar: member?.avatar || "/avatars/student.png"
                        },
                        memberData: member,
                        action,
                        book: bookTitle,
                        time: timeAgo
                    };
                });

                setActivities(formattedActivities);
            } catch (error) {
                console.error("Failed to fetch loans:", error);
            }
        };

        if (API_BASE && token && code_etablissement) {
            fetchLoans();
        }
    }, [API_BASE, token, code_etablissement, books, members, currentUser]);

    return (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full">
            <h3 className="text-lg font-bold text-white mb-6">Activité Récente</h3>
            <div className="space-y-2">
                {activities.length > 0 ? (
                    activities.map((activity, index) => (
                        <ActivityItem
                            key={index}
                            {...activity}
                            delay={index * 0.1}
                            onClick={() => handleMemberClick(activity.memberData)}
                        />
                    ))
                ) : (
                    <p className="text-slate-400 text-sm text-center py-4">Aucune activité récente</p>
                )}
            </div>
        </div>
    );
}
